import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Grid, TextField, MenuItem, Divider, Button, IconButton, TableBody, TableRow, TableCell, Table, TableHead, Checkbox, TextareaAutosize, TableSortLabel } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { handleSave } from "../utils/handleSave";
import { axios_tenant } from "../../../../axios";
import { COUNTRIES, US_STATES } from "../utils/constants";
import { ENTITY_STATUS_OPTIONS } from "../utils/entityStatus";
import AddIcon from "@mui/icons-material/Add";
import { FIXED_SELECT_FIELD_SX, TABLE_SCROLL_CONTAINER_SX, KOT_SELECT_MENU_PROPS, hasValue, isRowComplete } from "../utils/formHelpers";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import { formatKotDateTime } from "../utils/dateHelpers";
import { useKotFormDirty } from "../utils/useKotFormDirty";
import { notifyRequired } from "../../../utils/toast";

const sectionHasData = (rows = []) => (Array.isArray(rows) ? rows : []).some((row) => Object.entries(row || {}).some(([field, value]) => field !== "id" && hasValue(value)));

// A correspondence row counts as filled in if any of these carry a value.
const correspondentRowHasData = (row) => ["correspondenceType", "name", "email", "phone", "familyNo", "notes"].some((field) => hasValue(row?.[field]));

const ClientDetails = ({ mode, users, userRolesMap, roleData, setRoleData, setPrimaryUserIds }) => {
  const isView = mode === "view";
  const fullClientData = useSelector((state) => state.kotClient);
  const savedData = useSelector((state) => state.kotClient.clientDetails);
  const clientId = useSelector((state) => state.kotClient?.clientId);
  const licensedFamilies = useSelector((state) => state.kotClient.licensedFamilies);
  const statusByFamily = useSelector((state) => state.kotClient.statusByFamily);
  const paralegalInstructions = useSelector((state) => state.kotClient.paralegalInstructions);
  const ppsInstructions = useSelector((state) => state.kotClient.ppsInstructions);
  const annuties = useSelector((state) => state.kotClient.annuties);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const { isDirty, markDirty, markSaved, markReset, syncClean } = useKotFormDirty();
  const syncedClientIdRef = useRef(null);
  const createTempId = () => {
    try {
      return crypto?.randomUUID ? crypto.randomUUID() : `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    } catch (e) {
      return `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }
  };

  const getBlankFormData = () => ({
    clientName: "",
    sourceOfClientName: "",
    clientNumber: "",
    usptoCustomerNumber: "21971",
    address: "",
    phone: "",
    entityStatus: "",
    entityStatusSource: "",
    corporationCountry: "",
    corporationState: "",
    executedPoa: "",
    executedPoaLocation: "",
    assignmentCountry: "",
    assignmentState: "",
    signatory: "",
    signatoryTitle: "",
    pps: "",
    ffs: "",
    usPatentParalegal: "",
    lastUpdatedOn: "",
    correspondanceTo: [],
    documentUrls: [],
  });

  const buildDocumentRows = (documents = []) =>
    documents.length > 0
      ? documents.map((doc) => ({
          id: doc.id || createTempId(),
          documentName: doc.documentName || "",
          url: doc.url || "",
          isEditing: false,
        }))
      : [
          {
            id: createTempId(),
            documentName: "",
            url: "",
            isEditing: true,
          },
        ];

  const [documentRows, setDocumentRows] = useState(buildDocumentRows(savedData?.documentUrls || []));

  const [selectedDocumentRows, setSelectedDocumentRows] = useState([]);

  const buildFormData = (details) => ({
    ...getBlankFormData(),
    ...(details || {}),
    ...{
      pps: fullClientData?.assignments?.find((a) => a.assignmentRole === "PPS" && a.isPrimary)?.user?.id,
      ffs: fullClientData?.assignments?.find((a) => a.assignmentRole === "FFS" && a.isPrimary)?.user?.id,
      usPatentParalegal: fullClientData?.assignments?.find((a) => a.assignmentRole === "US Patent Paralegal" && a.isPrimary)?.user?.id,
    },
  });

  const [formData, setFormData] = useState(buildFormData(savedData));

  const buildCorrespondentRows = (rows = []) =>
    rows.length > 0
      ? [...rows]
          .sort((a, b) => {
            const order = { to: 0, cc: 1 };
            return (order[a.correspondenceType] ?? 2) - (order[b.correspondenceType] ?? 2);
          })
          .map((row) => ({ ...row }))
      : [{ id: createTempId(), correspondenceType: "", name: "", email: "", phone: "", familyNo: "", notes: "" }];

  const [correspondentData, setCorrespondentData] = useState(savedData?.correspondanceTo?.length > 0 ? buildCorrespondentRows(savedData.correspondanceTo) : buildCorrespondentRows());
  const [originalCorrespondentData, setOriginalCorrespondentData] = useState(savedData?.correspondanceTo || []);
  const [correspondentErrors, setCorrespondentErrors] = useState([]);

  const [sortOrder, setSortOrder] = useState("asc");
  const [sortBy, setSortBy] = useState(null);

  const [submitAttempted, setSubmitAttempted] = useState(false);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const alphanumericCollator = useMemo(() => new Intl.Collator(undefined, { numeric: true, sensitivity: "base" }), []);

  const sortedCorrespondentData = useMemo(() => {
    if (!sortBy) return correspondentData;
    return [...correspondentData].sort((a, b) => {
      const valA = (a[sortBy] ?? "").toString().trim();
      const valB = (b[sortBy] ?? "").toString().trim();
      const cmp = alphanumericCollator.compare(valA, valB);
      return sortOrder === "asc" ? cmp : -cmp;
    });
  }, [correspondentData, sortBy, sortOrder, alphanumericCollator]);

  useEffect(() => {
    // Sync from Redux (initial fetch + post-save server IDs) on client switch
    // or while the form is clean. Skip while dirty: saveData() runs multiple
    // sequential handleSave calls, and the Redux update from the clientDetails
    // save must not wipe unsaved correspondence edits or clear the dirty flag
    // before the correspondence save finishes.
    const clientChanged = syncedClientIdRef.current !== clientId;
    if (!clientChanged && isDirty) return;
    syncedClientIdRef.current = clientId;

    setFormData(buildFormData(savedData));

    const nextCorr = savedData?.correspondanceTo || [];
    setOriginalCorrespondentData(nextCorr.map((row) => ({ ...row })));
    setCorrespondentData(buildCorrespondentRows(nextCorr));
    setCorrespondentErrors(buildCorrespondentRows(nextCorr).map(() => ({})));
    setSelectedRows([]);

    setDocumentRows(buildDocumentRows(savedData?.documentUrls || []));
    setSelectedDocumentRows([]);
    syncClean();
  }, [clientId, savedData, isDirty, syncClean]);

  const handleDocumentChange = (id, field, value) => {
    markDirty();

    setDocumentRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  };

  const addDocumentRow = () => {
    if (documentRows.some((row) => row.isEditing && !row.url?.trim())) {
      toast.warn("Complete the current row before adding another.");
      return;
    }

    markDirty();
    setDocumentRows([
      ...documentRows,
      {
        id: createTempId(),
        documentName: "",
        url: "",
        isEditing: true,
      },
    ]);
  };

  const saveDocumentLink = (id) => {
    const row = documentRows.find((r) => r.id === id);
    try {
      row?.url && new URL(row.url);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    setDocumentRows((prev) => prev.map((r) => (r.id === id ? { ...r, isEditing: false } : r)));
  };

  const editDocumentLink = (id) => {
    setDocumentRows((prev) => prev.map((r) => (r.id === id ? { ...r, isEditing: true } : r)));
  };

  const handleDocumentSelectRow = (id) => {
    setSelectedDocumentRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  };

  const deleteSelectedDocumentRows = () => {
    markDirty();
    setDocumentRows((prev) => prev.filter((row) => !selectedDocumentRows.includes(row.id)));

    setSelectedDocumentRows([]);
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
  };

  const deleteSelectedRows = () => {
    setCorrespondentData((prev) => prev.filter((row) => !selectedRows.includes(row.id)));
    setCorrespondentErrors((prev) => prev.filter((_, index) => !selectedRows.includes(correspondentData[index].id)));
    setSelectedRows([]);
    markDirty();
  };

  const addCorrespondent = () => {
    const allRowsHaveData = correspondentData.every((row) => correspondentRowHasData(row));

    if (!allRowsHaveData) {
      toast.error("Each correspondence row must contain at least one value.");
      return;
    }
    setCorrespondentData([...correspondentData, { id: createTempId(), correspondenceType: "", name: "", email: "", phone: "", familyNo: "", notes: "" }]);
    setCorrespondentErrors([...correspondentErrors, {}]);
    markDirty();
  };

  const handleCorrespondentChange = (id, field, value) => {
    let processedValue = value;

    setCorrespondentData((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: processedValue } : row)));
    markDirty();

    // Validate
    setCorrespondentErrors((prev) => {
      const newErrors = [...prev];
      const rowIndex = correspondentData.findIndex((r) => r.id === id);
      if (rowIndex === -1) return prev;
      if (!newErrors[rowIndex]) newErrors[rowIndex] = {};

      if (field === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        newErrors[rowIndex].email = processedValue && !emailRegex.test(processedValue) ? "Invalid email address" : "";
      }

      if (field === "notes") {
        newErrors[rowIndex].notes = processedValue.length > 250 ? "Maximum 250 characters" : "";
      }

      return newErrors;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => {
      let updated = { ...prev, [name]: value };

      if (name === "corporationCountry" && value !== "US") {
        updated.corporationState = "";
      }

      if (name === "assignmentCountry" && value !== "US") {
        updated.assignmentState = "";
      }

      if (name === "executedPoa" && value !== "yes") {
        updated.executedPoaLocation = "";
      }

      return updated;
    });

    markDirty();
  };

  const isUSIncorporation = formData.corporationCountry === "US";
  const isUSAssignment = formData.assignmentCountry === "US";

  const buildAssignmentsFromDetails = () => {
    const rolesMap = {
      PPS: formData.pps,
      FFS: formData.ffs,
      "US Patent Paralegal": formData.usPatentParalegal,
    };

    const assignments = [];

    Object.entries(rolesMap).forEach(([role, userId]) => {
      if (userId) {
        assignments.push({
          userId,
          role,
          isPrimary: true, // ALWAYS primary
        });
      }
    });

    return assignments;
  };

  const mergeWithExistingAssignments = (primaryAssignments) => {
    if (!fullClientData?.isPrivate) return primaryAssignments;

    const merged = [];
    const roleUserMap = {};

    // Step 1: Add existing users
    Object.entries(roleData).forEach(([role, users]) => {
      roleUserMap[role] = users.map((u) => u.personid);
    });

    // Step 2: Ensure primary users exist
    primaryAssignments.forEach((pa) => {
      if (!roleUserMap[pa.role]) {
        roleUserMap[pa.role] = [];
      }
      if (!roleUserMap[pa.role].includes(pa.userId)) {
        roleUserMap[pa.role].push(pa.userId);
      }
    });

    // Step 3: Build final list
    Object.entries(roleUserMap).forEach(([role, userIds]) => {
      userIds.forEach((userId) => {
        const isPrimary = primaryAssignments.some((pa) => pa.role === role && pa.userId === userId);

        merged.push({
          userId,
          role,
          isPrimary,
        });
      });
    });

    return merged;
  };

  const syncAssignmentsToUI = (assignments) => {
    const updatedRoleData = {};
    const updatedPrimary = {};

    assignments.forEach((a) => {
      if (!updatedRoleData[a.role]) {
        updatedRoleData[a.role] = [];
      }

      const userObj = users.find((u) => u.personid === a.userId);

      if (userObj) {
        updatedRoleData[a.role].push(userObj);
      }

      if (a.isPrimary) {
        updatedPrimary[a.role] = a.userId;
      }
    });

    setRoleData(updatedRoleData);
    setPrimaryUserIds(updatedPrimary);
  };

  const saveData = async () => {
    setSubmitAttempted(true);

    if (!formData.clientName?.trim() || !formData.clientNumber?.trim()) {
      notifyRequired("kot-required-fields", "Missing required fields.");
      return;
    }

    setSubmitAttempted(false);
    setIsSaving(true);

    try {
      const currentTimestamp = new Date().toISOString();
      if (isUSIncorporation && !formData.corporationState) {
        notifyRequired("kot-corporationState-required", "Please select a US State for State of Incorporation.");
        return;
      }

      if (isUSAssignment && !formData.assignmentState) {
        notifyRequired("kot-assignmentState-required", "Please select a US State for Assignment Governing Law (Paragraph 7).");
        return;
      }

      const mappedDetails = {
        clientLegalName: formData.clientName || "",
        sourceOfClientName: formData.sourceOfClientName || "",
        usptoCustomerNumber: formData.usptoCustomerNumber || "",
        address: formData.address || "",
        phoneNumber: formData.phone || "",
        entityStatus: formData.entityStatus || "",
        sourceOfEntityStatus: formData.entityStatusSource || "",
        incorporationCountry: formData.corporationCountry || "",
        incorporationState: formData.corporationState || "",
        poaExecuted: formData.executedPoa || "",
        poaLocation: formData.executedPoaLocation || "",
        assignmentCountry: formData.assignmentCountry || "",
        assignmentState: formData.assignmentState || "",
        signatory: formData.signatory || "",
        signatoryTitle: formData.signatoryTitle || "",
        lastUpdatedOn: currentTimestamp,
        documentUrls: documentRows,
      };

      const primaryAssignments = buildAssignmentsFromDetails();
      const finalAssignments = mergeWithExistingAssignments(primaryAssignments);

      if (!clientId || clientId === "new") {
        // Create mode
        const payload = {
          clientName: formData.clientName || "",
          clientNumber: formData.clientNumber || "",
          isPrivate: false,
          assignments: [],
          ...mappedDetails,
        };
        const response = await axios_tenant.post("/kot/client-profiles", payload);

        const newClientId = response.data?.id;
        if (newClientId) {
          await axios_tenant.put(`/kot/client-profiles/${newClientId}/assignments`, { assignments: finalAssignments });
          syncAssignmentsToUI(finalAssignments);
          // Send 1-to-N correspondence rows
          const correspondenceResult = await handleSave(dispatch, "correspondence", correspondentData, newClientId, null, { showSuccessToast: false, showErrorToast: false });
          markSaved();
          // After fully creating, redirect the user into Edit mode!
          navigate(`/kot/client/edit/${newClientId}/client-details`, {
            state: {
              saveSuccessMessage: correspondenceResult?.status === "error" ? undefined : "Client created successfully.",
              saveErrorMessage: correspondenceResult?.status === "error" ? "Client was created, but correspondence details could not be saved." : undefined,
            },
          });
        }
      } else {
        // Edit mode
        const updatedFormData = {
          ...formData,
          documentUrls: documentRows,
        };
        const detailsResult = await handleSave(dispatch, "clientDetails", updatedFormData, clientId, savedData, { showSuccessToast: false });
        const correspondenceResult = await handleSave(dispatch, "correspondence", correspondentData, clientId, originalCorrespondentData, { showSuccessToast: false });

        const results = [detailsResult, correspondenceResult];
        if (results.some((result) => result?.status === "error")) {
          return;
        }

        await axios_tenant.put(`/kot/client-profiles/${clientId}/assignments`, { assignments: finalAssignments });
        syncAssignmentsToUI(finalAssignments);
        markSaved();

        if (results.some((result) => result?.status === "saved")) {
          toast.success("Client details saved successfully.");
          globalThis.dispatchEvent(new Event("refresh-client-profile"));
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save client details.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    markReset();

    const resetDetails = buildFormData(savedData);
    const resetCorrespondence = savedData?.correspondanceTo || [];

    setFormData(resetDetails);
    setOriginalCorrespondentData(resetCorrespondence.map((row) => ({ ...row })));
    setCorrespondentData(buildCorrespondentRows(resetCorrespondence));
    setCorrespondentErrors(buildCorrespondentRows(resetCorrespondence).map(() => ({})));
    setSelectedRows([]);
    setDocumentRows(buildDocumentRows(savedData?.documentUrls || []));
    setSelectedDocumentRows([]);
  };

  const additionalSections = [sectionHasData(licensedFamilies) ? "Related Parties" : null, [statusByFamily, paralegalInstructions, ppsInstructions].some(sectionHasData) ? "Shadowing & Monitoring" : null, sectionHasData(annuties) ? "Annuities" : null].filter(Boolean);
  const isUserEligibleForRole = (userId, assignmentRole) => {
    const roles = userRolesMap[userId] || [];
    if (roles.includes("ADMIN")) return true;

    // Attempt fuzzy matching between assignment role and KOT admin roles
    const searchStr = assignmentRole.toUpperCase().replace(/\s+/g, "_");
    return roles.some((r) => {
      const dbRole = r.toUpperCase();
      return dbRole === searchStr || dbRole.includes(searchStr) || searchStr.includes(dbRole) || (searchStr.includes("PARALEGAL") && dbRole.includes("PARALEGAL"));
    });
  };
  return (
    <Box p={4} bgcolor="#fff" borderRadius={2}>
      {/* Title */}
      <Typography variant="h3" mb={3}>
        Client Details
      </Typography>

      <Grid container spacing={3}>
        {/* Client Legal Name */}
        <Grid item xs={12} md={6}>
          <TextField required label="Client Legal Name" name="clientName" value={formData.clientName} onChange={handleChange} fullWidth variant="outlined" disabled={isView} error={submitAttempted && !formData.clientName?.trim()} helperText={submitAttempted && !formData.clientName?.trim() ? "Client Legal Name is required." : ""} />
        </Grid>

        {/* Source of Client Name */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} label="Source of Client Name" name="sourceOfClientName" value={formData.sourceOfClientName} onChange={handleChange} fullWidth />
        </Grid>

        {/* Client Number */}
        <Grid item xs={12} md={6}>
          <TextField required disabled={isView} label="Client Number" name="clientNumber" value={formData.clientNumber} onChange={handleChange} fullWidth error={submitAttempted && !formData.clientNumber?.trim()} helperText={submitAttempted && !formData.clientNumber?.trim() ? "Client Number is required." : ""} />
        </Grid>

        {/* USPTO Customer Number */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} label="USPTO Customer Number" name="usptoCustomerNumber" value={formData.usptoCustomerNumber} onChange={handleChange} fullWidth />
        </Grid>

        {/* Address */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} label="Address" name="address" value={formData.address} onChange={handleChange} multiline minRows={1} fullWidth />
        </Grid>

        {/* Phone */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} label="Phone No." name="phone" value={formData.phone} onChange={handleChange} fullWidth />
        </Grid>

        {/* Entity Status */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} select label="Entity Status" name="entityStatus" value={formData.entityStatus} onChange={handleChange} fullWidth>
            <MenuItem value="">Select</MenuItem>
            {ENTITY_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Source of Entity Status */}
        <Grid item xs={12} md={6}>
          <TextField
            disabled={isView}
            label="Entity Status Source / Notes"
            name="entityStatusSource"
            value={formData.entityStatusSource}
            onChange={handleChange}
            fullWidth
            multiline
            maxRows={6}
            sx={{
              "& .MuiInputBase-root": {
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
              "& .MuiInputBase-input": {
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            }}
          />
        </Grid>
      </Grid>

      {/* State of Incorporation */}
      <Box mt={4} mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          State of Incorporation
        </Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>

      <Grid container spacing={3}>
        {/* Country */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} select label="Select Country" name="corporationCountry" value={formData.corporationCountry} onChange={handleChange} fullWidth SelectProps={KOT_SELECT_MENU_PROPS}>
            <MenuItem value="">Select</MenuItem>
            {Object.entries(COUNTRIES).map(([key, country]) => (
              <MenuItem key={country} value={country}>
                {key}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* US State */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView || !isUSIncorporation} required={isUSIncorporation} select label="Select US State" name="corporationState" value={formData.corporationState} onChange={handleChange} fullWidth SelectProps={KOT_SELECT_MENU_PROPS}>
            <MenuItem value="">Select</MenuItem>
            {US_STATES.map((state, index) => (
              <MenuItem key={index} value={state}>
                {state}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* Executed POA */}
      <Box mt={4} mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          POA
        </Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} select label="Executed POA on file?" name="executedPoa" value={formData.executedPoa} onChange={handleChange} fullWidth>
            <MenuItem value="">Select</MenuItem>
            <MenuItem value="US">US</MenuItem>
            <MenuItem value="PCT">PCT</MenuItem>
            <MenuItem value="US & PCT">US & PCT</MenuItem>
          </TextField>
        </Grid>

        {/* Location */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} label=" Please specify location" name="executedPoaLocation" value={formData.executedPoaLocation} onChange={handleChange} fullWidth />
        </Grid>
      </Grid>

      {/* Assignment Para 7 */}
      <Box mt={4} mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          Assignment Governing Law (Paragraph 7)
        </Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>

      <Grid container spacing={3}>
        {/* Country */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} select label="Select Country" name="assignmentCountry" value={formData.assignmentCountry} onChange={handleChange} fullWidth SelectProps={KOT_SELECT_MENU_PROPS}>
            <MenuItem value="">Select</MenuItem>
            {Object.entries(COUNTRIES).map(([key, country]) => (
              <MenuItem key={country} value={country}>
                {key}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* US State */}
        <Grid item xs={12} md={6}>
          <TextField disabled={isView || !isUSAssignment} required={isUSAssignment} select label="Select US State" name="assignmentState" value={formData.assignmentState} onChange={handleChange} fullWidth SelectProps={KOT_SELECT_MENU_PROPS}>
            <MenuItem value="">Select</MenuItem>
            {US_STATES.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Box mt={4} mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          Signatory
        </Typography>
        <Divider sx={{ mt: 1 }} />
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            disabled={isView}
            label="Signatory & Title for Formal Docs"
            name="signatoryTitle"
            value={formData.signatoryTitle}
            onChange={handleChange}
            fullWidth
            multiline
            maxRows={6}
            sx={{
              "& .MuiInputBase-root": {
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
              "& .MuiInputBase-input": {
                whiteSpace: "normal",
                wordBreak: "break-word",
              },
            }}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField disabled={isView} select label="PPS" name="pps" value={formData.pps} onChange={handleChange} fullWidth>
            <MenuItem value="">Select</MenuItem>
            {[...users?.filter((u) => isUserEligibleForRole(u.personid, "PPS"))]
              .sort((a, b) => {
                const firstNameCompare = a.first_name.localeCompare(b.first_name);
                return firstNameCompare !== 0 ? firstNameCompare : a.last_name.localeCompare(b.last_name);
              })
              .map((user) => (
                <MenuItem key={user.personid} value={user.personid}>
                  {user.first_name} {user.last_name}
                </MenuItem>
              ))}
          </TextField>
        </Grid>

        {fullClientData?.isPrivate ? (
          <>
            <Grid item xs={12} md={6}>
              <TextField disabled={isView} select label="FFS" name="ffs" value={formData.ffs} onChange={handleChange} fullWidth>
                <MenuItem value="">Select</MenuItem>
                {[...users?.filter((u) => isUserEligibleForRole(u.personid, "FFS"))]
                  .sort((a, b) => {
                    const firstNameCompare = a.first_name.localeCompare(b.first_name);
                    return firstNameCompare !== 0 ? firstNameCompare : a.last_name.localeCompare(b.last_name);
                  })
                  .map((user) => (
                    <MenuItem key={user.personid} value={user.personid}>
                      {user.first_name} {user.last_name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField disabled={isView} select label="US Patent Paralegal" name="usPatentParalegal" value={formData.usPatentParalegal} onChange={handleChange} fullWidth>
                <MenuItem value="">Select</MenuItem>
                {[...users?.filter((u) => isUserEligibleForRole(u.personid, "US Patent Paralegal"))]
                  .sort((a, b) => {
                    const firstNameCompare = a.first_name.localeCompare(b.first_name);
                    return firstNameCompare !== 0 ? firstNameCompare : a.last_name.localeCompare(b.last_name);
                  })
                  .map((user) => (
                    <MenuItem key={user.personid} value={user.personid}>
                      {user.first_name} {user.last_name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
          </>
        ) : null}
      </Grid>

      <Grid item xs={12} md={6} mt={4}>
        <TextField disabled label="Last Updated On" name="lastUpdatedOn" value={formatKotDateTime(formData.lastUpdatedOn, "")} fullWidth />
        <Divider sx={{ my: 2 }} />
      </Grid>

      <Box mt={4} mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600}>
            Client Reference Documents
          </Typography>

          {!isView && selectedDocumentRows.length > 0 && (
            <Button variant="contained" color="error" onClick={deleteSelectedDocumentRows}>
              Delete ({selectedDocumentRows.length})
            </Button>
          )}
        </Box>

        <Box mt={2} sx={{ ...TABLE_SCROLL_CONTAINER_SX, p: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox data-ignore-dirty="true" disabled={isView} checked={documentRows.length > 0 && selectedDocumentRows.length === documentRows.length} indeterminate={selectedDocumentRows.length > 0 && selectedDocumentRows.length < documentRows.length} onChange={(e) => setSelectedDocumentRows(e.target.checked ? documentRows.map((r) => r.id) : [])} />
                </TableCell>

                <TableCell>Reference Document Link</TableCell>
                <TableCell width={620}>Document Name</TableCell>
                {!isView && <TableCell width={100}>Action</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {documentRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell padding="checkbox">
                    <Checkbox data-ignore-dirty="true" disabled={isView} checked={selectedDocumentRows.includes(row.id)} onChange={() => handleDocumentSelectRow(row.id)} />
                  </TableCell>
                  <TableCell>
                    {isView || !row.isEditing ? (
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          wordBreak: "break-all",
                        }}
                      >
                        {row.url}
                      </a>
                    ) : (
                      <TextareaAutosize
                        minRows={2}
                        value={row.url}
                        onChange={(e) => handleDocumentChange(row.id, "url", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #ddd",
                          borderRadius: "5px",
                        }}
                        placeholder="Reference Document Link"
                      />
                    )}
                  </TableCell>
                  <TableCell>{isView || !row.isEditing ? row.documentName : <TextField fullWidth placeholder="Document Name" value={row.documentName} onChange={(e) => handleDocumentChange(row.id, "documentName", e.target.value)} />}</TableCell>

                  {!isView && (
                    <TableCell>
                      {row.isEditing ? (
                        <IconButton color="primary" onClick={() => saveDocumentLink(row.id)}>
                          <CheckIcon />
                        </IconButton>
                      ) : (
                        <IconButton color="primary" onClick={() => editDocumentLink(row.id)}>
                          <EditIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {!isView && (
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <IconButton
              onClick={addDocumentRow}
              sx={{
                border: "1px solid #ddd",
                borderRadius: 2,
              }}
            >
              <AddIcon color="primary" />
            </IconButton>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
      </Box>
      {/* Correspondance to  */}
      <Box mt={4} mb={2}>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={600}>
            Correspondence to:
          </Typography>
          {!isView && selectedRows.length > 0 && (
            <Button variant="contained" color="error" onClick={deleteSelectedRows}>
              Delete ({selectedRows.length})
            </Button>
          )}
        </Box>
        <Box mt={2} sx={{ ...TABLE_SCROLL_CONTAINER_SX, p: 2 }}>
          <Table sx={{ minWidth: 1310, tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox data-ignore-dirty="true" disabled={isView} checked={selectedRows.length === correspondentData.length && correspondentData.length > 0} indeterminate={selectedRows.length > 0 && selectedRows.length < correspondentData.length} onChange={(e) => setSelectedRows(e.target.checked ? correspondentData.map((r) => r.id) : [])} size="medium" />
                </TableCell>
                <TableCell sx={{ width: 130 }}>Correspondance Type</TableCell>
                <TableCell sx={{ width: 260 }}>Name</TableCell>
                <TableCell sx={{ width: 320 }}>Email Address</TableCell>
                <TableCell sx={{ width: 150 }}>
                  <TableSortLabel active={sortBy === "phone"} direction={sortBy === "phone" ? sortOrder : "asc"} onClick={() => handleSort("phone")}>
                    Phone Number
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 150 }}>
                  <TableSortLabel active={sortBy === "familyNo"} direction={sortBy === "familyNo" ? sortOrder : "asc"} onClick={() => handleSort("familyNo")}>
                    Family No.
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 270, minWidth: 220 }}>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCorrespondentData.map((row, index) => {
                const errorIndex = correspondentData.findIndex((r) => r.id === row.id);
                const errors = correspondentErrors[errorIndex] || {};
                return (
                  <TableRow key={row.id}>
                    <TableCell padding="checkbox">
                      <Checkbox data-ignore-dirty="true" disabled={isView} checked={selectedRows.includes(row.id)} onChange={() => handleSelectRow(row.id)} size="medium" />
                    </TableCell>
                    <TableCell>
                      <TextField disabled={isView} fullWidth sx={FIXED_SELECT_FIELD_SX} label="Select" select size="small" value={row.correspondenceType} onChange={(e) => handleCorrespondentChange(row.id, "correspondenceType", e.target.value)}>
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="to">To</MenuItem>
                        <MenuItem value="cc">CC</MenuItem>
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField disabled={isView} fullWidth multiline minRows={1} label="Name" size="small" value={row.name} onChange={(e) => handleCorrespondentChange(row.id, "name", e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField disabled={isView} fullWidth multiline minRows={1} label="Email Address" size="small" value={row.email} onChange={(e) => handleCorrespondentChange(row.id, "email", e.target.value)} error={!!errors.email} helperText={errors.email} />
                    </TableCell>
                    <TableCell>
                      <TextField disabled={isView} fullWidth multiline minRows={1} label="Phone Number" size="small" value={row.phone} onChange={(e) => handleCorrespondentChange(row.id, "phone", e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField disabled={isView} fullWidth multiline minRows={1} label="Family No." size="small" value={row.familyNo} onChange={(e) => handleCorrespondentChange(row.id, "familyNo", e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField disabled={isView} fullWidth multiline minRows={1} label="Notes" size="small" value={row.notes} onChange={(e) => handleCorrespondentChange(row.id, "notes", e.target.value)} inputProps={{ maxLength: 250 }} error={!!errors.notes} helperText={errors.notes} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        {!isView && (
          <Box display={"flex"} alignItems={"center"} justifyContent={"flex-end"} mt={2}>
            <IconButton onClick={addCorrespondent} size="medium" sx={{ border: "1px solid #ddd", borderRadius: 2 }}>
              <AddIcon color="primary" />
            </IconButton>
          </Box>
        )}
      </Box>

      {additionalSections.length > 0 && (
        <Box mt={4}>
          <Typography variant="h3" fontWeight={400} mb={2}>
            Additional information is available in other sections of this client profile.
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {additionalSections.map((sectionName) => (
              <Typography variant="h5" component="li" key={sectionName} sx={{ mb: 1.5 }}>
                {sectionName}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {/* Footer Buttons — disabled until the form has unsaved changes */}
      {!isView && (
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="text" onClick={handleCancel} disabled={isSaving || !isDirty}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveData} disabled={isSaving || !isDirty}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ClientDetails;
